// Ported from dev-scrips-pm-react src/billing/lib/clinical-note-vitals.ts (PROD-1119 cross-module closure).
import type { NoteVital } from '@/billing/services/use-clinical-note';
import { bmiFromWeightHeight } from '@/encounter/vitals-history/vital-derive';

/**
 * Vitals list for the Clinical Note panel (PROD-969c phase 3) — the fixed,
 * ordered list Flutter renders in `VitalsComponentWidget` / `emptyVitalUiList`
 * (`ui_data.dart`). LOINC ids are copied verbatim from
 * `app_constants.dart:259-275`. Labels/units/order match the Flutter list,
 * including the verbatim `"02 Sat"` label.
 *
 * Conditional rows (age/gender), per `emptyVitalUiList`:
 *   - LMP (8665-2)               — female AND age > 8
 *   - Head Circumference (11947-9) — age <= 2
 *
 * BMI (always a whole number): the server never stores a BMI row for vitals entered outside PA, so when
 * there is none it is computed from height + weight (`bmiFromWeightHeight`, the
 * vitals-history rule: whole number, implausible results suppressed).
 *
 * NOTE: there is no React shared LOINC/vitals constants module today, so these
 * ids are transcribed verbatim from the cited Flutter source (the spec's source
 * of truth — there is no second React copy to drift against). If another React
 * surface needs vital codes, promote this map to `@/shared/` rather than copy.
 */

// LOINC ids (app_constants.dart)
const TEMP = '8310-5';
const PULSE = '8867-4';
const SYSTOLIC = '8480-6';
const DIASTOLIC = '8462-4';
const RESP = '9279-1';
const O2 = '59408-5';
const HEIGHT = '8302-2';
const WEIGHT = '29463-7';
const BMI = '39156-5';
const HIP = '8281-8';
const WAIST = '8280-0';
const WAIST_HIP = '56115-9';
const BIOAGE = '11-5';
const VISUAL = '31479-8';
const HEARING = '62977-4';
const LMP = '8665-2';
const HCM = '11947-9';

type VitalKind = 'single' | 'bp' | 'pair';

interface VitalSpec {
  label: string;
  loinc: string;
  symbol: string;
  kind: VitalKind;
  /** second LOINC for the BP pair (diastolic). */
  loinc2?: string;
}

const BASE_VITALS: VitalSpec[] = [
  { label: 'Temperature', loinc: TEMP, symbol: '°C', kind: 'single' },
  { label: 'Pulse', loinc: PULSE, symbol: '', kind: 'single' },
  { label: 'BP', loinc: SYSTOLIC, loinc2: DIASTOLIC, symbol: '', kind: 'bp' },
  { label: 'Resp Rate', loinc: RESP, symbol: '', kind: 'single' },
  { label: '02 Sat', loinc: O2, symbol: '%', kind: 'single' },
  { label: 'Height', loinc: HEIGHT, symbol: 'cm', kind: 'single' },
  { label: 'Weight', loinc: WEIGHT, symbol: 'kg', kind: 'single' },
  { label: 'BMI', loinc: BMI, symbol: '', kind: 'single' },
  { label: 'Hip Circumference', loinc: HIP, symbol: 'cm', kind: 'single' },
  { label: 'Waist Circumference', loinc: WAIST, symbol: 'cm', kind: 'single' },
  { label: 'Waist-to-Hip Ratio', loinc: WAIST_HIP, symbol: '', kind: 'single' },
  { label: 'Bioage', loinc: BIOAGE, symbol: '', kind: 'single' },
  { label: 'Visual Acuity', loinc: VISUAL, symbol: '', kind: 'pair' },
  { label: 'Hearing Test', loinc: HEARING, symbol: '', kind: 'pair' },
];

export interface VitalRow {
  label: string;
  /** Display string — value+unit, or the empty placeholder. */
  value: string;
  abnormal: boolean;
}

/** Parse Flutter's `"1y 5m 1d"` / `"33y"` age text to whole years. */
export function ageYearsFromText(age?: string | null): number | undefined {
  if (!age) return undefined;
  const m = /(\d+)\s*y/.exec(age);
  return m ? Number(m[1]) : 0; // "5m" → 0 years
}

function findVital(
  vitals: NoteVital[],
  loinc: string,
): NoteVital | undefined {
  return vitals.find((v) => v.loincCode === loinc && v.quantityValue != null && v.quantityValue !== '');
}

function numericValue(vital: NoteVital | undefined): number | null {
  const n = Number(vital?.quantityValue);
  return Number.isFinite(n) ? n : null;
}

function emptyDisplay(spec: VitalSpec): string {
  if (spec.kind === 'bp' || spec.kind === 'pair') return '— /—';
  return spec.symbol ? `— ${spec.symbol}` : '—';
}

function valueDisplay(spec: VitalSpec, vitals: NoteVital[]): { value: string; abnormal: boolean } {
  if (spec.kind === 'bp') {
    const sys = findVital(vitals, spec.loinc);
    const dia = spec.loinc2 ? findVital(vitals, spec.loinc2) : undefined;
    if (!sys && !dia) return { value: emptyDisplay(spec), abnormal: false };
    return {
      value: `${sys?.quantityValue ?? '—'} /${dia?.quantityValue ?? '—'}`,
      abnormal:
        sys?.vitalNormalStatus === 'abnormal' || dia?.vitalNormalStatus === 'abnormal',
    };
  }
  const found = findVital(vitals, spec.loinc);
  if (!found && spec.loinc === BMI) {
    const bmi = bmiFromWeightHeight(
      numericValue(findVital(vitals, WEIGHT)),
      numericValue(findVital(vitals, HEIGHT)),
    );
    if (bmi != null) return { value: String(bmi), abnormal: false };
  }
  if (!found) return { value: emptyDisplay(spec), abnormal: false };
  // BMI is a whole number everywhere (Flutter `.roundToDouble()`), a stored row included.
  if (spec.loinc === BMI) {
    const stored = numericValue(found);
    if (stored != null) return { value: String(Math.round(stored)), abnormal: found.vitalNormalStatus === 'abnormal' };
  }
  const v = spec.symbol
    ? `${found.quantityValue} ${spec.symbol}`
    : `${found.quantityValue}`;
  return { value: v, abnormal: found.vitalNormalStatus === 'abnormal' };
}

/**
 * Build the ordered vital rows for display, applying the age/gender conditional
 * rows (Head Circumference ≤2y, LMP female>8) in the same positions Flutter
 * uses (LMP + Head Circumference appended after the base list).
 */
export function buildVitalRows(params: {
  vitals: NoteVital[];
  ageYears?: number;
  gender?: 'Male' | 'Female' | string | null;
}): VitalRow[] {
  const { vitals, ageYears, gender } = params;
  const specs = [...BASE_VITALS];
  if (gender === 'Female' && ageYears != null && ageYears > 8) {
    specs.push({ label: 'LMP', loinc: LMP, symbol: '', kind: 'single' });
  }
  if (ageYears != null && ageYears <= 2) {
    specs.push({ label: 'Head Circumference', loinc: HCM, symbol: 'cm', kind: 'single' });
  }
  return specs.map((spec) => {
    const { value, abnormal } = valueDisplay(spec, vitals);
    return { label: spec.label, value, abnormal };
  });
}
